import type {
  PerformanceFilters,
  PerformanceMarketOption,
  PerformanceOptions,
  PerformanceRange,
  PerformanceSearchType,
  PerformanceTargetMarketOption,
  PerformanceUrlSearch,
  ResolvedPerformanceSelection,
  ResolvedPerformanceTree,
} from './performance.types'
import { appendTargetMarketIds, targetMarketIdsEqual } from './targetMarkets'

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

// Calendar-day span for each preset, ending on and including today.
const PRESET_WINDOW_DAYS: Record<'30d' | '90d' | '1y', number> = {
  '30d': 30,
  '90d': 90,
  '1y': 365,
}

export function parseUtcDate(value: string): Date | null {
  const match = DATE_ONLY_RE.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))

  const roundTrips =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day

  return roundTrips ? date : null
}

function utcMidnight(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
}

function addUtcDays(date: Date, days: number): Date {
  const shifted = new Date(date)
  shifted.setUTCDate(shifted.getUTCDate() + days)
  return shifted
}

export function performanceWindow(
  range: PerformanceRange,
  now: Date,
  custom?: { from: string; to: string }
): { from: string; to: string } | null {
  const today = utcMidnight(now)

  if (range === 'custom') {
    if (!custom) return null
    const from = parseUtcDate(custom.from)
    const to = parseUtcDate(custom.to)
    if (!from || !to || from.getTime() > to.getTime()) return null

    return { from: from.toISOString(), to: addUtcDays(to, 1).toISOString() }
  }

  const days = PRESET_WINDOW_DAYS[range]
  return {
    from: addUtcDays(today, -(days - 1)).toISOString(),
    to: addUtcDays(today, 1).toISOString(),
  }
}

function isPerformanceRange(value: string | null): value is PerformanceRange {
  return (
    value === '30d' || value === '90d' || value === '1y' || value === 'custom'
  )
}

// Falls a 'custom' range back to 90d whenever its window is missing or invalid.
function resolveRangeAndCustomDates(params: URLSearchParams): {
  range: PerformanceRange
  from: string | null
  to: string | null
} {
  const requested = params.get('range')
  const range = isPerformanceRange(requested) ? requested : '90d'
  if (range !== 'custom') return { range, from: null, to: null }

  const from = params.get('from')
  const to = params.get('to')
  if (!from || !to) return { range: '90d', from: null, to: null }

  const fromDate = parseUtcDate(from)
  const toDate = parseUtcDate(to)
  if (!fromDate || !toDate || fromDate.getTime() > toDate.getTime()) {
    return { range: '90d', from: null, to: null }
  }

  return { range: 'custom', from, to }
}

// Matches SQL lower(btrim(...)); plain toLowerCase (not toLocaleLowerCase) stays locale-independent.
function normalizeOptionKey(value: string): string {
  return value.trim().toLowerCase()
}

// Cascades brand -> category -> market, falling back to the first option per invalid level.
function resolveTree(
  options: PerformanceOptions,
  params: URLSearchParams
): ResolvedPerformanceTree | null {
  const brand =
    options.brands.find(
      (candidate) => candidate.value === params.get('brand')
    ) ?? options.brands[0]

  if (!brand) {
    return null
  }

  const categoryParam = params.get('category')
  const category =
    brand.categories.find(
      (candidate) =>
        categoryParam !== null &&
        candidate.value === normalizeOptionKey(categoryParam)
    ) ?? brand.categories[0]

  if (!category) {
    return null
  }

  const targetMarkets = category.targetMarkets ?? []
  const requestedTargetMarketIds = params
    .getAll('targetMarketIds')
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
  const targetMarket = findTargetMarket(
    targetMarkets,
    requestedTargetMarketIds,
    params.get('market')
  )
  const marketParam = params.get('market')
  const market = targetMarket
    ? null
    : (category.markets.find(
        (candidate) =>
          marketParam !== null &&
          candidate.value === normalizeOptionKey(marketParam)
      ) ?? category.markets[0])

  if (!market && !targetMarket) {
    const firstTargetMarket = targetMarkets[0]

    if (!firstTargetMarket) {
      return null
    }

    return { brand, category, market: null, targetMarket: firstTargetMarket }
  }

  return {
    brand,
    category,
    market: market ?? null,
    targetMarket: targetMarket ?? null,
  }
}

function findTargetMarket(
  targetMarkets: PerformanceTargetMarketOption[],
  requestedIds: string[],
  legacyMarket: string | null
): PerformanceTargetMarketOption | undefined {
  return targetMarkets.find(
    (candidate) =>
      targetMarketIdsEqual(candidate.targetMarketIds, requestedIds) ||
      candidate.targetMarketIds.join(',') === legacyMarket
  )
}

function toUrlSearch(type: PerformanceSearchType): PerformanceUrlSearch {
  return type === 'live_search' ? 'live' : 'standard'
}

function resolveSearch(
  market: PerformanceMarketOption | PerformanceTargetMarketOption,
  params: URLSearchParams
): PerformanceUrlSearch {
  const requested = params.get('search')
  let requestedType: PerformanceSearchType | null = null
  if (requested === 'live') requestedType = 'live_search'
  else if (requested === 'standard') requestedType = 'standard'

  const isValid =
    requestedType !== null && market.searchTypes.includes(requestedType)
  return toUrlSearch(
    isValid && requestedType ? requestedType : market.defaultSearchType
  )
}

export function resolvePerformanceUrl(
  options: PerformanceOptions,
  params: URLSearchParams,
  now: Date
): {
  selection: ResolvedPerformanceSelection | null
  normalizedParams: URLSearchParams
  filters: PerformanceFilters | null
} {
  const { range, from, to } = resolveRangeAndCustomDates(params)
  const tree = resolveTree(options, params)
  const normalizedParams = new URLSearchParams()

  if (tree) {
    normalizedParams.set('brand', tree.brand.value)
    normalizedParams.set('category', tree.category.value)

    if (tree.targetMarket) {
      appendTargetMarketIds(normalizedParams, tree.targetMarket.targetMarketIds)
    } else if (tree.market) {
      normalizedParams.set('market', tree.market.value)
    }
  }

  if (!tree) {
    normalizedParams.set('range', range)
    if (range === 'custom' && from && to) {
      normalizedParams.set('from', from)
      normalizedParams.set('to', to)
    }
    return { selection: null, normalizedParams, filters: null }
  }

  const selectedMarket = tree.targetMarket ?? tree.market

  if (!selectedMarket) {
    return { selection: null, normalizedParams, filters: null }
  }

  const search = resolveSearch(selectedMarket, params)

  normalizedParams.set('search', search)
  normalizedParams.set('range', range)
  if (range === 'custom' && from && to) {
    normalizedParams.set('from', from)
    normalizedParams.set('to', to)
  }

  const window =
    range === 'custom' && from && to
      ? performanceWindow(range, now, { from, to })
      : performanceWindow(range, now)

  const selection: ResolvedPerformanceSelection = {
    brandId: tree.brand.value,
    category: tree.category.value,
    market: tree.targetMarket
      ? tree.targetMarket.targetMarketIds.join(',')
      : (tree.market?.value ?? ''),
    targetMarketIds: tree.targetMarket?.targetMarketIds,
    search,
    range,
    from: range === 'custom' ? from : null,
    to: range === 'custom' ? to : null,
  }
  const filters: PerformanceFilters | null = window
    ? {
        brandId: tree.brand.value,
        category: tree.category.value,
        market: tree.targetMarket
          ? tree.targetMarket.targetMarketIds.join(',')
          : (tree.market?.value ?? ''),
        targetMarketIds: tree.targetMarket?.targetMarketIds,
        liveSearch: search === 'live',
        from: window.from,
        to: window.to,
      }
    : null

  return { selection, normalizedParams, filters }
}
